import ipc from '@/renderer/scripts/utils/ipc';
import { IpcEvents } from '@/ipc-events';

type Callback = (type?: any, params?: any) => void | Promise<void>;

type HandleCallback = (event: IpcEvents, ...args: any[]) => Promise<any>;

interface WindowIPCNotifyInfo {
  workerProcessIPCTaskId: number;
  workerProcessChannel: IpcEvents;
  workerWebContentsIdFrom: number;
  param: any; // 外部传的参数
  msgFromHandle?: boolean; // Handle IPC回调
  error?: any;
}

/**
 * @description 工作进程通信工具类
 * @export
 * @class WorkerProcessIPCHepler
 */
export default class WorkerProcessIPCHepler {
  private sourceId = 0;
  private dstId: number;
  private handleCallbackMap: Map<IpcEvents, HandleCallback> = new Map<IpcEvents, HandleCallback>();
  private defaultTimeout = 1000 * 60 * 5; // 默认超时时间
  private static ipcTaskId = Date.now();
  static readonly WORKER_PROCESS_TIMEOUT_ERROR = 'WorkerProcess Timeout';

  constructor(sourceId: number, dstId: number) {
    this.sourceId = sourceId;
    this.dstId = dstId;
  }

  init() {
    ipc.on(IpcEvents.WORKER_WINDOW_IPC_NOTIFY, this.onWindowIPCNotify);
  }

  unInit() {
    ipc.off(IpcEvents.WORKER_WINDOW_IPC_NOTIFY, this.onWindowIPCNotify);
  }

  // 外部需要处理抛异常场景, timeOut设置超时时间，默认5分钟，单位毫秒
  invoke(channel: IpcEvents, param: any, timeOut?: number) {
    return new Promise((resolve, reject) => {
      try {
        const taskId = WorkerProcessIPCHepler.ipcTaskId++;
        const reqData: WindowIPCNotifyInfo = {
          workerProcessIPCTaskId: taskId,
          workerProcessChannel: channel,
          workerWebContentsIdFrom: this.sourceId,
          param,
        };

        let timer: NodeJS.Timeout | undefined = undefined;
        const waitCallBack = (e: any, info: WindowIPCNotifyInfo) => {
          if (taskId !== info.workerProcessIPCTaskId) {
            return;
          }
          if (!info.msgFromHandle) {
            return;
          }
          clearTimeout(timer);
          ipc.off(IpcEvents.WORKER_WINDOW_IPC_NOTIFY, waitCallBack);

          if (info.error) {
            reject(new Error(info.error));
          } else {
            resolve(info.param);
          }
        };

        timer = setTimeout(
          () => {
            logger.ui.error('invoke timeout', channel, param);
            ipc.off(IpcEvents.WORKER_WINDOW_IPC_NOTIFY, waitCallBack);
            reject(new Error(WorkerProcessIPCHepler.WORKER_PROCESS_TIMEOUT_ERROR));
          },
          timeOut ? timeOut : this.defaultTimeout,
        );

        ipc.on(IpcEvents.WORKER_WINDOW_IPC_NOTIFY, waitCallBack);
        ipc.sendTo(this.dstId, IpcEvents.WORKER_WINDOW_IPC_NOTIFY, reqData);
      } catch (error) {
        logger.ui.info('WORKER_WINDOW_IPC_NOTIFY invoke error', error, channel, param);
        reject(error);
      }
    });
  }

  senTo(channel: IpcEvents, ...args: Array<any>) {
    ipc.sendTo(this.dstId, channel, ...args);
  }

  on(channel: IpcEvents, callback: Callback): WorkerProcessIPCHepler {
    ipc.on(channel, callback);
    return this;
  }

  Off(channel: IpcEvents, callback: Callback) {
    ipc.off(channel, callback);
  }

  handle(channel: IpcEvents, listener: HandleCallback) {
    this.handleCallbackMap.set(channel, listener);
  }

  offHandle(channel: IpcEvents) {
    this.handleCallbackMap.delete(channel);
  }

  private onWindowIPCNotify = async (e: any, info: WindowIPCNotifyInfo) => {
    if (!info.workerProcessIPCTaskId || !info.workerWebContentsIdFrom) {
      logger.ui.info('WORKER_WINDOW_IPC_NOTIFY onWindowIPCNotify wrong param', info);
      return;
    }
    if (this.dstId !== info.workerWebContentsIdFrom) {
      logger.ui.info('WORKER_WINDOW_IPC_NOTIFY onWindowIPCNotify wrong dstId', info);
      return;
    }

    if (info.msgFromHandle) {
      return;
    }

    const respData: WindowIPCNotifyInfo = {
      workerProcessIPCTaskId: info.workerProcessIPCTaskId,
      workerProcessChannel: info.workerProcessChannel,
      workerWebContentsIdFrom: this.sourceId,
      param: undefined,
      msgFromHandle: true,
    };
    try {
      const callBack = this.handleCallbackMap.get(info.workerProcessChannel);
      if (!callBack) {
        logger.ui.info('WORKER_WINDOW_IPC_NOTIFY no callBack', info);
        return;
      }
      respData.param = await callBack(info.workerProcessChannel, info.param);
    } catch (error) {
      logger.ui.info('WORKER_WINDOW_IPC_NOTIFY error', error, info);
      respData.error = error;
    }

    ipc.sendTo(info.workerWebContentsIdFrom, IpcEvents.WORKER_WINDOW_IPC_NOTIFY, respData);
  };
}
